import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        recipient: 'info@arteryvailofficial.com'
      });
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      {/* Hero */}
      <section className="relative h-[40vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1600"
            alt="Contact ARTERYVAIL"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 to-[#0A0A0A]/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="font-serif text-5xl md:text-7xl text-[#F5F2EB] mb-4">
              Get in Touch
            </h1>
            <p className="text-lg text-[#A3988F]">
              We'd love to hear from you. Reach out and let's create something extraordinary together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-4xl text-[#F5F2EB] mb-8">Let's Connect</h2>
              <p className="text-[#A3988F] mb-12 leading-relaxed">
                Whether you're interested in nutrition consultation, media production, or fashion design, 
                our team is here to bring your vision to life.
              </p>

              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#FAD6C6]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#FAD6C6]" />
                  </div>
                  <div>
                    <h3 className="text-[#F5F2EB] font-semibold mb-1">Email</h3>
                    <a href="mailto:info@arteryvailofficial.com" className="text-[#A3988F] hover:text-[#FAD6C6] transition-colors">
                      info@arteryvailofficial.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#FAD6C6]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#FAD6C6]" />
                  </div>
                  <div>
                    <h3 className="text-[#F5F2EB] font-semibold mb-1">Serving</h3>
                    <p className="text-[#A3988F]">Clients Worldwide</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-8 luxury-card rounded-xl">
                <h3 className="text-[#F5F2EB] font-serif text-2xl mb-4">Office Hours</h3>
                <div className="space-y-2 text-[#A3988F]">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p>Saturday: 10:00 AM - 4:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {submitted ? (
                <div className="luxury-card rounded-xl p-8 text-center">
                  <div className="w-20 h-20 bg-[#FAD6C6]/10 rounded-full flex items-center justify-center mx-auto mb-8">
                    <Send className="w-10 h-10 text-[#FAD6C6]" />
                  </div>
                  <h3 className="font-serif text-3xl text-[#F5F2EB] mb-4">Message Sent!</h3>
                  <p className="text-[#A3988F]">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="luxury-card rounded-xl p-8 space-y-6">
                  <h3 className="font-serif text-2xl text-[#F5F2EB] mb-6">Send us a Message</h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-[#A3988F] mb-2">Full Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="luxury-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#A3988F] mb-2">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="luxury-input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="luxury-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Subject *</label>
                    <select
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="luxury-select"
                      required
                    >
                      <option value="">Select a topic</option>
                      <option value="Nutrition Inquiry">Nutrition Inquiry</option>
                      <option value="Media Services">Media Services</option>
                      <option value="Fashion Inquiry">Fashion Inquiry</option>
                      <option value="General Question">General Question</option>
                      <option value="Partnership">Partnership</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Message *</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="luxury-input h-40 resize-none"
                      placeholder="Tell us how we can help you..."
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <div className="loader w-5 h-5" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
